/**
 * Cocuze wishlist — localStorage; guest vs logged-in customer keys; merge on login.
 */
(function () {
  const PREFIX = 'cocuze_wishlist_v1';
  const GUEST_KEY = PREFIX + '_guest';

  function getRoot() {
    return document.getElementById('cocuze-wishlist-root');
  }

  function getCustomerId() {
    const r = getRoot();
    return r && r.dataset.customerId ? String(r.dataset.customerId).trim() : '';
  }

  function storageKey() {
    const cid = getCustomerId();
    return cid ? PREFIX + '_c_' + cid : GUEST_KEY;
  }

  function readList() {
    try {
      const raw = localStorage.getItem(storageKey());
      if (!raw) return [];
      const data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(items) {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(items));
    } catch (e) {
      /* quota or private mode */
    }
  }

  /** Merge guest wishlist into customer list once, then clear guest. */
  function mergeGuestIfNeeded() {
    const cid = getCustomerId();
    if (!cid) return;
    try {
      const guestRaw = localStorage.getItem(GUEST_KEY);
      if (!guestRaw) return;
      const guest = JSON.parse(guestRaw);
      if (!Array.isArray(guest) || guest.length === 0) return;
      const mine = readList();
      const seen = new Set(mine.map((x) => x.handle));
      guest.forEach((g) => {
        if (g && g.handle && !seen.has(g.handle)) {
          seen.add(g.handle);
          mine.push({ handle: g.handle, id: g.id || null, added: g.added || Date.now() });
        }
      });
      writeList(mine);
      localStorage.removeItem(GUEST_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function handlesSet() {
    return new Set(readList().map((x) => x.handle));
  }

  function isSaved(handle) {
    return handlesSet().has(handle);
  }

  function toggle(handle, id) {
    const list = readList();
    const idx = list.findIndex((x) => x.handle === handle);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      list.push({ handle, id: id || null, added: Date.now() });
    }
    writeList(list);
    syncAllButtons();
    updateHeaderCount();
    document.dispatchEvent(
      new CustomEvent('cocuze-wishlist-change', { detail: { count: list.length } })
    );
    return idx < 0;
  }

  function remove(handle) {
    const list = readList().filter((x) => x.handle !== handle);
    writeList(list);
    syncAllButtons();
    updateHeaderCount();
    document.dispatchEvent(new CustomEvent('cocuze-wishlist-change', { detail: { count: list.length } }));
  }

  function syncAllButtons() {
    const set = handlesSet();
    document.querySelectorAll('[data-cocuze-wishlist-toggle]').forEach((btn) => {
      const h = btn.getAttribute('data-product-handle');
      if (!h) return;
      const on = set.has(h);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = on ? btn.getAttribute('data-label-remove') : btn.getAttribute('data-label-add');
      if (label) btn.setAttribute('aria-label', label);
    });
  }

  function updateHeaderCount() {
    const n = readList().length;
    document.querySelectorAll('[data-cocuze-wishlist-count]').forEach((el) => {
      el.textContent = n > 0 ? String(n) : '';
      el.setAttribute('data-count', String(n));
    });
  }

  function formatMoney(cents, currency) {
    const v = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(v);
    } catch (e) {
      return currency + ' ' + v.toFixed(2);
    }
  }

  function drawerEls() {
    return {
      drawer: document.getElementById('cocuze-wishlist-drawer'),
      list: document.getElementById('cocuze-wishlist-drawer-list'),
      empty: document.getElementById('cocuze-wishlist-drawer-empty'),
    };
  }

  function setDrawerOpen(open) {
    const { drawer } = drawerEls();
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.classList.toggle('cocuze-wishlist-drawer-open', open);
    if (open) {
      renderDrawerList();
      const closeBtn = drawer.querySelector('.cocuze-wishlist-drawer__close');
      if (closeBtn) closeBtn.focus();
    }
  }

  async function fetchProduct(handle) {
    const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
    if (!res.ok) throw new Error('fetch');
    return res.json();
  }

  async function renderDrawerList() {
    const { list, empty } = drawerEls();
    if (!list || !empty) return;
    const items = readList();
    if (items.length === 0) {
      list.innerHTML = '';
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    const root = getRoot();
    const loadingText = (root && root.dataset.loadingText) || 'Loading…';
    list.innerHTML = '<p class="cocuze-wishlist-drawer__loading">' + loadingText + '</p>';
    const currency = (root && root.dataset.currency) || 'USD';

    const rows = await Promise.all(
      items.map(async (entry) => {
        try {
          const p = await fetchProduct(entry.handle);
          let img = '';
          if (p.featured_image) {
            img = typeof p.featured_image === 'string' ? p.featured_image : p.featured_image.src || '';
          } else if (p.images && p.images.length) {
            const first = p.images[0];
            img = typeof first === 'string' ? first : first.src || '';
          }
          const price = p.price_min != null ? p.price_min : p.price;
          const priceStr = formatMoney(price, currency);
          const url = p.url || '/products/' + p.handle;
          const title = p.title || entry.handle;
          return (
            '<div class="cocuze-wishlist-row" data-row-handle="' +
            escapeAttr(entry.handle) +
            '">' +
            (img
              ? '<a class="cocuze-wishlist-row__media" href="' +
                escapeAttr(url) +
                '"><img src="' +
                escapeAttr(img) +
                '" alt="" width="144" height="144" loading="lazy"></a>'
              : '<div class="cocuze-wishlist-row__media"></div>') +
            '<div class="cocuze-wishlist-row__body">' +
            '<p class="cocuze-wishlist-row__title"><a href="' +
            escapeAttr(url) +
            '">' +
            escapeHtml(title) +
            '</a></p>' +
            '<p class="cocuze-wishlist-row__price">' +
            escapeHtml(priceStr) +
            '</p>' +
            '<button type="button" class="cocuze-wishlist-row__remove" data-cocuze-wishlist-remove="' +
            escapeAttr(entry.handle) +
            '">' +
            escapeHtml((root && root.dataset.removeText) || 'Remove') +
            '</button>' +
            '</div></div>'
          );
        } catch (e) {
          return (
            '<div class="cocuze-wishlist-row">' +
            '<div class="cocuze-wishlist-row__body">' +
            '<p class="cocuze-wishlist-row__title">' +
            escapeHtml(entry.handle) +
            '</p>' +
            '<button type="button" class="cocuze-wishlist-row__remove" data-cocuze-wishlist-remove="' +
            escapeAttr(entry.handle) +
            '">' +
            escapeHtml((root && root.dataset.removeText) || 'Remove') +
            '</button></div></div>'
          );
        }
      })
    );
    list.innerHTML = rows.join('');
  }

  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  document.addEventListener('click', function (e) {
    const t = e.target;
    const openBtn = t.closest('[data-cocuze-open-wishlist]');
    if (openBtn) {
      e.preventDefault();
      setDrawerOpen(true);
      return;
    }

    const closeBtn = t.closest('[data-cocuze-wishlist-close]');
    if (closeBtn) {
      setDrawerOpen(false);
      return;
    }

    const overlay = t.closest('.cocuze-wishlist-drawer__overlay');
    if (overlay) {
      setDrawerOpen(false);
      return;
    }

    const wl = t.closest('[data-cocuze-wishlist-toggle]');
    if (wl) {
      e.preventDefault();
      e.stopPropagation();
      const handle = wl.getAttribute('data-product-handle');
      const id = wl.getAttribute('data-product-id');
      if (!handle) return;
      toggle(handle, id);
      return;
    }

    const rm = t.closest('[data-cocuze-wishlist-remove]');
    if (rm) {
      const h = rm.getAttribute('data-cocuze-wishlist-remove');
      if (h) {
        remove(h);
        renderDrawerList();
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    const { drawer } = drawerEls();
    if (drawer && drawer.classList.contains('is-open')) setDrawerOpen(false);
  });

  function init() {
    mergeGuestIfNeeded();
    syncAllButtons();
    updateHeaderCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function syncUi() {
    syncAllButtons();
    updateHeaderCount();
  }

  window.CocuzeWishlist = {
    readList,
    toggle,
    remove,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
    sync: syncUi,
  };
})();
